import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudentSession } from '../context/StudentContext';
import { apiGetStudentDashboard, apiRaiseComplaint, apiGetStudentComplaints, apiGetJobBoard, apiApplyToJob, apiUpdateInterviewStatus, apiSubmitPaymentDate } from '../lib/studentApi';
import MyTasksTab from './MyTasksTab';

// ── Types ──────────────────────────────────────────────────────────────────────
interface ProjectStatus { phase: number; status: 'locked' | 'available' | 'completed'; }
interface DayItem       { day: number; topic: string; }
interface Checkpoint    { phase: number; afterDay: number; difficulty: string; description: string; }
interface PlacementStage { stageIndex: number; stageName: string; status: string; completedDate?: string; }
interface Complaint     { _id: string; title: string; description: string; status: string; createdAt: string; history: any[]; }

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IcoCheck = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
    <polyline points="3 8 6.5 11.5 13 4.5" />
  </svg>
);
const IcoLock = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
    <rect x="3" y="7" width="10" height="7" rx="1.5" />
    <path d="M5 7V5a3 3 0 016 0v2" />
  </svg>
);
const IcoClip = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
    <path d="M10.5 3H6a3 3 0 000 6h5a2 2 0 000-4H6a1 1 0 000 2h4.5" />
  </svg>
);
const IcoChevron = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="w-4 h-4 transition-transform duration-200" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
    <polyline points="4 6 8 10 12 6" />
  </svg>
);
const IcoArrow = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <line x1="3" y1="8" x2="13" y2="8" /><polyline points="9 4 13 8 9 12" />
  </svg>
);

// ── Status config ──────────────────────────────────────────────────────────────
const COMPLAINT_STATUS: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  'Open':        { label: 'Open',        dot: '#ef4444', bg: '#fef2f2', text: '#b91c1c' },
  'In Progress': { label: 'In Progress', dot: '#f59e0b', bg: '#fffbeb', text: '#92400e' },
  'Needs Info':  { label: 'Needs Info',  dot: '#6366f1', bg: '#eef2ff', text: '#3730a3' },
  'Resolved':    { label: 'Resolved',    dot: '#10b981', bg: '#ecfdf5', text: '#065f46' },
};
const DIFF_STYLE: Record<string, { bg: string; text: string }> = {
  Easy:   { bg: '#ecfdf5', text: '#065f46' },
  Medium: { bg: '#fffbeb', text: '#92400e' },
  Hard:   { bg: '#fef2f2', text: '#b91c1c' },
};
const PROJECT_STATUS_STYLE = {
  locked:    { bg: '#f8fafc', border: '#e2e8f0', text: '#94a3b8', badge: '#f1f5f9', badgeText: '#64748b' },
  available: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', badge: '#fef3c7', badgeText: '#b45309' },
  completed: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', badge: '#dcfce7', badgeText: '#15803d' },
};

// ── Circular Arc Progress ──────────────────────────────────────────────────────
function ArcProgress({ pct, size = 180 }: { pct: number; size?: number }) {
  const cx = size / 2, cy = size / 2;
  const r = (size / 2) - 16;
  const circ = 2 * Math.PI * r;
  const dashOffset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ overflow: 'visible' }}>
      {/* Track */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
      {/* Progress */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#arc-g)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)' }} />
      <defs>
        <linearGradient id="arc-g" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F0B429" />
          <stop offset="100%" stopColor="#FF6535" />
        </linearGradient>
      </defs>
      {/* Center text */}
      <text x={cx} y={cy - 6} textAnchor="middle" fill="#0f172a"
        style={{ fontWeight: 900, fontSize: 32, fontFamily: 'inherit' }}>{pct}%</text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill="#94a3b8"
        style={{ fontWeight: 600, fontSize: 11, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1 }}>
        Complete
      </text>
    </svg>
  );
}

// ── Thin Progress Bar ──────────────────────────────────────────────────────────
function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="w-full h-1.5 rounded-full" style={{ background: '#e2e8f0' }}>
      <div className="h-1.5 rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#F0B429,#FF6535)' }} />
    </div>
  );
}

// ── Section Label ──────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#94a3b8' }}>{children}</p>
  );
}

// ── Progress Tab ───────────────────────────────────────────────────────────────
function ProgressTab({ progress, stats }: { progress: any; stats: any }) {
  const { curriculumTitle, totalDays, completedDays, pct, message,
          days, projectCheckpoints, projectStatuses, assessmentUnlocked } = progress;

  if (!totalDays) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
        <div className="w-12 h-12 rounded-2xl mb-5 flex items-center justify-center" style={{ background: '#f1f5f9' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
        </div>
        <p className="font-bold text-slate-700 mb-1">No curriculum assigned yet</p>
        <p className="text-sm text-slate-400 leading-relaxed">Your coordinator will assign your course curriculum. Check back soon.</p>
      </div>
    );
  }

  const cpMap: Record<number, Checkpoint> = {};
  (projectCheckpoints || []).forEach((cp: Checkpoint) => { cpMap[cp.afterDay] = cp; });
  const statusMap: Record<number, ProjectStatus> = {};
  (projectStatuses || []).forEach((ps: ProjectStatus) => { statusMap[ps.phase] = ps; });

  const statCards = [
    { label: 'Days Completed', value: completedDays.length, sub: `of ${totalDays}` },
    { label: 'Absences',       value: stats.absences,       sub: 'total' },
    { label: 'Tasks Given',    value: stats.tasksGiven,     sub: 'by mentor' },
    { label: 'Tasks Done',     value: stats.tasksDone,      sub: 'completed' },
  ];

  return (
    <div className="px-4 pt-6 pb-10 space-y-8">

      {/* Arc + motivational */}
      <div className="flex flex-col items-center gap-2">
        <ArcProgress pct={pct} />
        <div className="text-center px-6">
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#F0B429' }}>{curriculumTitle}</p>
          <p className="text-sm text-slate-500 font-medium leading-relaxed">{message}</p>
        </div>
      </div>

      {/* Stats */}
      <div>
        <SectionLabel>Activity Overview</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          {statCards.map((s, i) => (
            <div key={s.label} className="rounded-2xl p-4"
              style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
              <div className="flex items-end justify-between mb-2">
                <span className="text-3xl font-black" style={{ color: i === 1 ? '#ef4444' : '#0f172a' }}>{s.value}</span>
                <span className="text-xs text-slate-400 font-medium pb-1">{s.sub}</span>
              </div>
              <p className="text-xs font-semibold text-slate-500">{s.label}</p>
              <div className="mt-2">
                <ProgressBar pct={i === 0 ? pct : i === 3 ? (stats.tasksGiven ? Math.round((stats.tasksDone / stats.tasksGiven) * 100) : 0) : 0} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assessment unlocked */}
      {assessmentUnlocked && (
        <div className="rounded-2xl px-5 py-4" style={{ background: 'linear-gradient(135deg,#F0B429 0%,#FF6535 100%)' }}>
          <p className="text-sm font-black text-white">Second Assessment Unlocked</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>All 4 projects are complete. Contact your coordinator to schedule.</p>
        </div>
      )}

      {/* Day-by-day */}
      <div>
        <SectionLabel>Course Timeline</SectionLabel>
        <div className="rounded-2xl overflow-hidden"
          style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            {(days || []).map((d: DayItem, idx: number) => {
              const done = completedDays.includes(d.day);
              const cp   = cpMap[d.day];
              const ps   = cp ? statusMap[cp.phase] : null;
              const isLast = idx === (days || []).length - 1;
              return (
                <React.Fragment key={d.day}>
                  <div className="flex items-center gap-4 px-4 py-3 transition-colors"
                    style={{ borderBottom: !isLast && !cp ? '1px solid #f8fafc' : undefined,
                             background: done ? '#fafffe' : '#fff' }}>
                    {/* Day dot */}
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                      style={{
                        background: done ? 'linear-gradient(135deg,#F0B429,#FF6535)' : '#f1f5f9',
                        color: done ? '#fff' : '#94a3b8',
                      }}>
                      {done ? <span className="w-3.5 h-3.5"><IcoCheck /></span> : d.day}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-bold" style={{ color: done ? '#F0B429' : '#cbd5e1' }}>
                          Day {String(d.day).padStart(2, '0')}
                        </span>
                        <span className="text-xs text-slate-400 truncate hidden sm:block">—</span>
                      </div>
                      <p className="text-sm font-medium leading-tight truncate"
                        style={{ color: done ? '#0f172a' : '#94a3b8' }}>{d.topic}</p>
                    </div>
                    {done && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: '#f0fdf4', color: '#15803d' }}>Done</span>
                    )}
                  </div>
                  {cp && <ProjectRow cp={cp} ps={ps} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectRow({ cp, ps }: { cp: Checkpoint; ps: ProjectStatus | null | undefined }) {
  const s = (ps?.status || 'locked') as keyof typeof PROJECT_STATUS_STYLE;
  const c = PROJECT_STATUS_STYLE[s];
  const labels = { locked: 'Locked', available: 'Available — Submit to Mentor', completed: 'Completed' };
  return (
    <div className="mx-3 my-1.5 rounded-xl px-4 py-3 flex items-center gap-3"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center" style={{ color: c.text }}>
        {s === 'completed' ? <IcoCheck /> : s === 'available' ? <IcoClip /> : <IcoLock />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-black" style={{ color: c.text }}>Project {cp.phase}</p>
        <p className="text-xs truncate" style={{ color: c.text, opacity: 0.75 }}>{labels[s]}</p>
      </div>
      <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
        style={{ background: DIFF_STYLE[cp.difficulty]?.bg || '#f1f5f9', color: DIFF_STYLE[cp.difficulty]?.text || '#64748b' }}>
        {cp.difficulty}
      </span>
    </div>
  );
}

// ── Placement Tab ──────────────────────────────────────────────────────────────
// Your task scores count towards placement: shown on top of the placement tab
function TaskScoreCard({ tasks }: { tasks?: { avgScore: number | null; reviewed: number; done: number; given: number; onTimePct: number | null } }) {
  if (!tasks || !tasks.reviewed) return null;
  return (
    <div className="rounded-2xl p-4 flex items-center gap-4" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black" style={{ background: '#fffbeb', color: '#b45309' }}>{tasks.avgScore}</div>
      <div className="flex-1">
        <SectionLabel>Task score</SectionLabel>
        <p className="text-sm text-slate-600">Average {tasks.avgScore}/10 over {tasks.reviewed} reviewed task{tasks.reviewed === 1 ? '' : 's'} · {tasks.done} of {tasks.given} done{tasks.onTimePct !== null ? ` · ${tasks.onTimePct}% on time` : ''}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">The placement team sees this. Keep it high!</p>
      </div>
    </div>
  );
}

function PlacementTab({ placement, tasks }: { placement: any; tasks?: any }) {
  if (!placement) {
    if (tasks?.reviewed) return <div className="px-4 pt-6"><TaskScoreCard tasks={tasks} /></div>;
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
        <div className="w-12 h-12 rounded-2xl mb-5 flex items-center justify-center" style={{ background: '#f1f5f9' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="font-bold text-slate-700 mb-1">Placement tracking not started</p>
        <p className="text-sm text-slate-400 leading-relaxed">Your placement journey begins after course completion. Stay focused!</p>
      </div>
    );
  }

  const stages: PlacementStage[] = placement.stages || [];
  const completedCount = stages.filter((s: PlacementStage) => s.status === 'completed').length;
  const pct = stages.length ? Math.round((completedCount / stages.length) * 100) : 0;

  return (
    <div className="px-4 pt-6 pb-10 space-y-8">
      <TaskScoreCard tasks={tasks} />
      {/* Summary */}
      <div className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <SectionLabel>Placement Progress</SectionLabel>
            <p className="text-3xl font-black" style={{ color: '#0f172a' }}>{completedCount} <span className="text-lg text-slate-300">/ {stages.length}</span></p>
            <p className="text-sm text-slate-400 mt-0.5">stages completed</p>
          </div>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: pct === 100 ? '#f0fdf4' : '#fffbeb' }}>
            <span className="text-xl font-black" style={{ color: pct === 100 ? '#15803d' : '#d97706' }}>{pct}%</span>
          </div>
        </div>
        <ProgressBar pct={pct} />
      </div>

      {/* Stage list */}
      <div>
        <SectionLabel>Stage Tracker</SectionLabel>
        <div className="space-y-2">
          {stages.map((stage: PlacementStage, i: number) => {
            const isDone    = stage.status === 'completed';
            const isCurrent = stage.status === 'in_progress';
            return (
              <div key={i} className="flex items-center gap-4 rounded-2xl px-4 py-4 transition-all"
                style={{
                  background: isDone ? '#f0fdf4' : isCurrent ? '#fffbeb' : '#fff',
                  border: `1px solid ${isDone ? '#bbf7d0' : isCurrent ? '#fde68a' : '#f1f5f9'}`,
                }}>
                {/* Stage number / check */}
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-sm"
                  style={{
                    background: isDone ? 'linear-gradient(135deg,#22c55e,#16a34a)' : isCurrent ? 'linear-gradient(135deg,#F0B429,#d97706)' : '#f1f5f9',
                    color: isDone || isCurrent ? '#fff' : '#94a3b8',
                  }}>
                  {isDone ? <span className="w-4 h-4"><IcoCheck /></span> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: isDone ? '#166534' : isCurrent ? '#92400e' : '#64748b' }}>
                    {stage.stageName}
                  </p>
                  {stage.completedDate && <p className="text-xs text-slate-400 mt-0.5">{stage.completedDate}</p>}
                  {isCurrent && <p className="text-xs font-semibold mt-0.5" style={{ color: '#d97706' }}>Currently in progress</p>}
                </div>
                {isCurrent && (
                  <div className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse" style={{ background: '#F0B429' }} />
                )}
                {isDone && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: '#dcfce7', color: '#15803d' }}>Done</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Job Board Tab ──────────────────────────────────────────────
interface JobItem {
  _id: string;
  title: string;
  company: string;
  description: string;
  jobLink: string;
  targetCourses: string[];
  postedByName: string;
  status: string;
  createdAt: string;
  lastDate?: string;   // YYYY-MM-DD — the job closes after this day
  applied: boolean;
  interviewStatus?: string;
}

// 'Apply by Mon, 12 Oct' — 'today' / 'tomorrow' when it's that close
function applyByLabel(lastDate: string) {
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
  const days = Math.round((Date.parse(`${lastDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  const label = new Date(`${lastDate}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  return days <= 0 ? 'Last day to apply: today' : days === 1 ? `Last day to apply: tomorrow, ${label}` : `Apply by ${label}`;
}

const INTERVIEW_STATUSES = ['Applied', 'Interview Scheduled', 'Interview Done', 'Offer Received', 'Rejected'];
const ISTATUS_STYLE: Record<string, { bg: string; text: string }> = {
  'Applied':             { bg: '#eff6ff', text: '#1d4ed8' },
  'Interview Scheduled': { bg: '#fefce8', text: '#854d0e' },
  'Interview Done':      { bg: '#f0fdf4', text: '#166534' },
  'Offer Received':      { bg: '#dcfce7', text: '#15803d' },
  'Rejected':            { bg: '#fef2f2', text: '#b91c1c' },
};

function JobBoardTab({ studentId }: { studentId: string }) {
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [screenshotJob, setScreenshotJob] = useState<JobItem | null>(null);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  useEffect(() => {
    apiGetJobBoard(studentId)
      .then(res => setJobs(res.jobs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  async function handleApplySubmit() {
    if (!screenshotJob || !screenshotFile) return;
    setSubmitting(true);
    setApplyError('');
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;
          await apiApplyToJob(studentId, screenshotJob._id, base64, screenshotFile.name);
          setJobs(prev => prev.map(j => j._id === screenshotJob._id ? { ...j, applied: true } : j));
          setScreenshotJob(null);
          setScreenshotFile(null);
        } catch (err: any) {
          setApplyError(err.message || 'Failed to submit');
        } finally {
          setSubmitting(false);
        }
      };
      reader.readAsDataURL(screenshotFile);
    } catch {
      setSubmitting(false);
    }
  }

  async function handleStatusUpdate(jobId: string, status: string) {
    setUpdatingStatus(jobId);
    try {
      await apiUpdateInterviewStatus(studentId, jobId, status);
      setJobs(prev => prev.map(j => j._id === jobId ? { ...j, interviewStatus: status } : j));
    } catch (err: any) {
      // silent fail — status will be shown as old value
    } finally {
      setUpdatingStatus(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-7 h-7 rounded-full border-2 border-slate-200 border-t-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-10 space-y-4">
      <SectionLabel>Job Openings</SectionLabel>

      {jobs.length === 0 ? (
        <div className="rounded-2xl p-12 flex flex-col items-center text-center"
          style={{ background: '#fff', border: '1px solid #f1f5f9' }}>
          <div className="w-12 h-12 rounded-2xl mb-4 flex items-center justify-center" style={{ background: '#f8fafc' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="font-bold text-slate-600 mb-1">No job openings yet</p>
          <p className="text-sm text-slate-400">New openings will appear here when posted by your placement coordinator.</p>
        </div>
      ) : (
        jobs.map(job => (
          <div key={job._id} className="rounded-2xl p-5 space-y-3" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-black text-base" style={{ color: '#0f172a' }}>{job.title}</p>
                <p className="text-sm font-medium text-slate-500">{job.company}</p>
                {job.lastDate && !job.applied && (
                  <p className="text-xs font-bold mt-1" style={{ color: '#b91c1c' }}>{applyByLabel(job.lastDate)}</p>
                )}
              </div>
              {job.applied && (
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">Applied ✓</span>
                  <select
                    value={job.interviewStatus ?? 'Applied'}
                    disabled={updatingStatus === job._id}
                    onChange={e => handleStatusUpdate(job._id, e.target.value)}
                    className="text-xs px-2 py-1 rounded-lg border font-semibold outline-none cursor-pointer"
                    style={{
                      backgroundColor: (ISTATUS_STYLE[job.interviewStatus ?? 'Applied'] ?? ISTATUS_STYLE['Applied']).bg,
                      color: (ISTATUS_STYLE[job.interviewStatus ?? 'Applied'] ?? ISTATUS_STYLE['Applied']).text,
                      borderColor: (ISTATUS_STYLE[job.interviewStatus ?? 'Applied'] ?? ISTATUS_STYLE['Applied']).text + '40',
                    }}
                  >
                    {INTERVIEW_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
            {job.description && (
              <p className="text-sm text-slate-600 leading-relaxed">{job.description}</p>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              {job.jobLink && (
                <a href={job.jobLink} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95"
                  style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#F0B429' }}>
                  Apply on Job Portal
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </a>
              )}
              {!job.applied && (
                <button
                  onClick={() => { setScreenshotJob(job); setApplyError(''); setScreenshotFile(null); }}
                  className="px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95"
                  style={{ background: '#f0fdf4', color: '#16a34a', border: '1.5px solid #bbf7d0' }}>
                  Upload Screenshot
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {/* Screenshot upload modal */}
      {screenshotJob && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6 space-y-4" style={{ background: '#fff' }}>
            <p className="font-black text-base" style={{ color: '#0f172a' }}>
              Submit Application — {screenshotJob.title}
            </p>
            <p className="text-sm text-slate-500">
              After applying on the job portal, upload a screenshot as proof of your application.
            </p>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b' }}>Screenshot *</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => setScreenshotFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-600 hover:file:bg-slate-200"
              />
            </div>
            {applyError && <p className="text-xs font-medium" style={{ color: '#ef4444' }}>{applyError}</p>}
            <div className="flex gap-3">
              <button onClick={() => { setScreenshotJob(null); setScreenshotFile(null); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleApplySubmit}
                disabled={!screenshotFile || submitting}
                className="flex-1 py-2.5 rounded-xl text-sm font-black transition-all active:scale-95 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#F0B429' }}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Complaints Tab ─────────────────────────────────────────────────────────────
function ComplaintsTab({ studentId }: { studentId: string }) {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [title, setTitle]           = useState('');
  const [desc, setDesc]             = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [expanded, setExpanded]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await apiGetStudentComplaints(studentId); setComplaints(res.complaints || []); }
    catch { } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    if (!title.trim() || !desc.trim()) { setError('Both fields are required.'); return; }
    setSaving(true);
    try { await apiRaiseComplaint(studentId, title.trim(), desc.trim()); setTitle(''); setDesc(''); setShowForm(false); load(); }
    catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  const open = complaints.filter(c => c.status !== 'Resolved').length;

  return (
    <div className="px-4 pt-6 pb-10 space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <SectionLabel>Complaints</SectionLabel>
          <p className="text-2xl font-black" style={{ color: '#0f172a' }}>{complaints.length}</p>
          <p className="text-xs text-slate-400">{open} open</p>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95"
          style={{
            background: showForm ? '#f1f5f9' : 'linear-gradient(135deg,#2b2240,#3d2d58)',
            color: showForm ? '#64748b' : '#fff',
          }}>
          {showForm ? 'Cancel' : 'Raise Complaint'}
          {!showForm && <span className="w-4 h-4"><IcoArrow /></span>}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 4px 16px rgba(0,0,0,.06)' }}>
          <p className="text-sm font-black mb-4" style={{ color: '#0f172a' }}>New Complaint</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#64748b' }}>Subject</label>
              <input value={title} onChange={e => setTitle(e.target.value)} required
                placeholder="What is this complaint about?"
                className="w-full px-4 py-3 rounded-xl text-sm transition-all outline-none"
                style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }}
                onFocus={e => { e.target.style.borderColor = '#F0B429'; e.target.style.boxShadow = '0 0 0 3px rgba(240,180,41,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#64748b' }}>Description</label>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} required rows={5}
                placeholder="Describe your issue clearly and in detail…"
                className="w-full px-4 py-3 rounded-xl text-sm resize-none transition-all outline-none"
                style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }}
                onFocus={e => { e.target.style.borderColor = '#F0B429'; e.target.style.boxShadow = '0 0 0 3px rgba(240,180,41,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }} />
            </div>
            {error && <p className="text-xs font-medium" style={{ color: '#ef4444' }}>{error}</p>}
            <button type="submit" disabled={saving}
              className="w-full py-3 rounded-xl text-sm font-black transition-all active:scale-95 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#fff' }}>
              {saving ? 'Submitting…' : 'Submit Complaint'}
            </button>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 rounded-full border-2 border-slate-200 border-t-yellow-400 animate-spin" />
        </div>
      ) : complaints.length === 0 ? (
        <div className="rounded-2xl p-12 flex flex-col items-center text-center"
          style={{ background: '#fff', border: '1px solid #f1f5f9' }}>
          <div className="w-12 h-12 rounded-2xl mb-4 flex items-center justify-center" style={{ background: '#f8fafc' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <p className="font-bold text-slate-600 mb-1">No complaints raised</p>
          <p className="text-sm text-slate-400">Use the button above if you have any concerns.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => {
            const cs = COMPLAINT_STATUS[c.status] || { label: c.status, dot: '#94a3b8', bg: '#f8fafc', text: '#64748b' };
            const isOpen = expanded === c._id;
            return (
              <div key={c._id} className="rounded-2xl overflow-hidden transition-all"
                style={{ background: '#fff', border: `1px solid ${isOpen ? '#e2e8f0' : '#f1f5f9'}`,
                         boxShadow: isOpen ? '0 4px 16px rgba(0,0,0,.06)' : '0 1px 2px rgba(0,0,0,.03)' }}>
                {/* Row */}
                <button className="w-full text-left px-5 py-4 flex items-center gap-4"
                  onClick={() => setExpanded(isOpen ? null : c._id)}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cs.dot }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{c.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0"
                    style={{ background: cs.bg, color: cs.text }}>{cs.label}</span>
                  <span style={{ color: '#94a3b8' }}><IcoChevron open={isOpen} /></span>
                </button>

                {/* Expanded */}
                {isOpen && (
                  <div className="px-5 pb-5 border-t" style={{ borderColor: '#f8fafc' }}>
                    <p className="text-sm text-slate-600 mt-4 mb-5 leading-relaxed">{c.description}</p>
                    {(c.history || []).length > 0 && (
                      <>
                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#94a3b8' }}>Status History</p>
                        <div className="relative pl-4">
                          {/* Vertical line */}
                          <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: '#f1f5f9' }} />
                          <div className="space-y-4">
                            {(c.history || []).map((h: any, i: number) => {
                              const hc = COMPLAINT_STATUS[h.status] || { label: h.status, dot: '#94a3b8', bg: '#f8fafc', text: '#64748b' };
                              return (
                                <div key={i} className="relative">
                                  <div className="absolute -left-5 top-1 w-2.5 h-2.5 rounded-full"
                                    style={{ background: hc.dot, border: '2px solid #fff' }} />
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-xs font-bold" style={{ color: hc.text }}>{hc.label}</span>
                                    <span className="text-xs text-slate-400">
                                      · {new Date(h.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                    </span>
                                  </div>
                                  {h.note && <p className="text-xs text-slate-500 leading-relaxed">{h.note}</p>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Next fee payment date ──────────────────────────────────────────────────────
// Shown once a student who still owes part of their fee has attended 5
// classes: they pick the date of their next payment and admin confirms it.
interface PaymentDateRequestView {
  _id: string;
  status: 'Asked' | 'Pending' | 'Approved' | 'Rejected' | 'Scheduled';
  requestedDate: string;
  studentNote: string;
  dueDate: string;
  adminNote: string;
}
interface FeePayment {
  courseFee: number;
  totalPaid: number;
  balance: number;
  classesAttended: number;
  canPick: boolean;
  request: PaymentDateRequestView | null;
  today: string;
  maxDate: string;
}

const rupees  = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDay  = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

function FeePaymentCard({ studentId, fee, onChange }: {
  studentId: string; fee: FeePayment; onChange: (fee: FeePayment) => void;
}) {
  const req = fee.request;
  const [editing, setEditing] = useState(!req || req.status === 'Asked');
  const [date, setDate]       = useState(req?.requestedDate || '');
  const [note, setNote]       = useState(req?.studentNote || '');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    if (!date) { setError('Please choose a date.'); return; }
    setSaving(true);
    try {
      const res = await apiSubmitPaymentDate(studentId, date, note.trim());
      onChange({ ...fee, request: res.request });
      setEditing(false);
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  };

  const fixed   = req && ['Approved', 'Rejected', 'Scheduled'].includes(req.status);
  const overdue = fixed && req!.dueDate < fee.today;
  const dueNow  = fixed && req!.dueDate === fee.today;

  // Date agreed — what to pay and by when
  if (fixed && req) {
    const accent = overdue ? '#dc2626' : req.status === 'Rejected' ? '#d97706' : '#16a34a';
    const title  = overdue ? '⏰ Your fee payment is overdue'
      : dueNow ? '📅 Your fee payment is due today'
      : req.status === 'Approved' ? '✅ Your payment date is approved'
      : req.status === 'Rejected' ? '📅 Please pay by the date below'
      : '📅 Your next payment date';
    return (
      <div className="mx-4 mt-5 rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #f1f5f9', borderLeft: `4px solid ${accent}`, boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <p className="text-sm font-black" style={{ color: accent }}>{title}</p>
        {req.status === 'Rejected' && (
          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            The date you asked for ({fmtDay(req.requestedDate)}) was not approved, but we have given you time till {fmtDay(req.dueDate)}.
          </p>
        )}
        <div className="flex items-end justify-between mt-3 gap-3">
          <div>
            <p className="text-xs text-slate-400">Pay on or before</p>
            <p className="text-lg font-black" style={{ color: '#0f172a' }}>{fmtDay(req.dueDate)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Balance</p>
            <p className="text-lg font-black" style={{ color: '#2b2240' }}>{rupees(fee.balance)}</p>
          </div>
        </div>
        {req.adminNote && (
          <p className="text-xs mt-3 px-3 py-2 rounded-xl" style={{ background: '#f8fafc', color: '#475569' }}>
            <span className="font-bold">Note from VistaTech: </span>{req.adminNote}
          </p>
        )}
      </div>
    );
  }

  // Waiting for admin
  if (req && req.status === 'Pending' && !editing) {
    return (
      <div className="mx-4 mt-5 rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #f1f5f9', borderLeft: '4px solid #F0B429', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-black" style={{ color: '#92400e' }}>⏳ Waiting for approval</p>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              You asked to pay your balance of <b>{rupees(fee.balance)}</b> on <b>{fmtDay(req.requestedDate)}</b>.
              We'll email you once it's confirmed.
            </p>
          </div>
          <button onClick={() => setEditing(true)}
            className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
            style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
            Change
          </button>
        </div>
      </div>
    );
  }

  // Pick a date
  return (
    <div className="mx-4 mt-5 rounded-2xl p-5" style={{ background: '#fff', border: '1.5px solid #fde68a', boxShadow: '0 4px 16px rgba(240,180,41,.15)' }}>
      <p className="text-sm font-black" style={{ color: '#0f172a' }}>📅 Choose your next fee payment date</p>
      <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
        You've completed {fee.classesAttended} classes — great going! Your fee balance is <b>{rupees(fee.balance)}</b>.
        Pick the date you'll make your next payment and our team will confirm it.
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#64748b' }}>Payment date</label>
          <input type="date" value={date} min={fee.today} max={fee.maxDate} onChange={e => setDate(e.target.value)} required
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#64748b' }}>Note (optional)</label>
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={500}
            placeholder="e.g. I receive my stipend on the 10th"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }} />
        </div>
        {error && <p className="text-xs font-medium" style={{ color: '#ef4444' }}>{error}</p>}
        <div className="flex gap-2">
          {req?.status === 'Pending' && (
            <button type="button" onClick={() => { setEditing(false); setError(''); }}
              className="flex-1 py-3 rounded-xl text-sm font-bold border-2 border-gray-200 text-gray-600">
              Cancel
            </button>
          )}
          <button type="submit" disabled={saving}
            className="flex-1 py-3 rounded-xl text-sm font-black transition-all active:scale-95 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#F0B429' }}>
            {saving ? 'Submitting…' : 'Submit date'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
type Tab = 'progress' | 'tasks' | 'placement' | 'jobs' | 'complaints';

const TAB_CONFIG: { key: Tab; label: string; icon: React.ReactNode }[] = [
  {
    key: 'progress', label: 'Progress',
    icon: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4"><polyline points="2 14 7 8 11 12 18 4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  },
  {
    key: 'tasks', label: 'Tasks',
    icon: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M7 4H5a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1h-2M7 4a1 1 0 001 1h4a1 1 0 001-1M7 4a1 1 0 011-1h4a1 1 0 011 1M7 11l2 2 4-4" /></svg>,
  },
  {
    key: 'placement', label: 'Placement',
    icon: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M10 2a6 6 0 016 6c0 3.5-4.5 9.5-6 10C8.5 17.5 4 11.5 4 8a6 6 0 016-6z" /><circle cx="10" cy="8" r="2" /></svg>,
  },
  {
    key: 'jobs', label: 'Jobs',
    icon: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M6 6V4a2 2 0 012-2h4a2 2 0 012 2v2M3 6h14l-1 11H4L3 6zM10 11v4" /></svg>,
  },
  {
    key: 'complaints', label: 'Complaints',
    icon: <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h14a1 1 0 011 1v8a1 1 0 01-1 1H5l-3 3V5a1 1 0 011-1z" /></svg>,
  },
];

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { session, signOut } = useStudentSession();
  const [tab, setTab]         = useState<Tab>('progress');
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) { navigate('/student/login'); return; }
    apiGetStudentDashboard(session.studentId)
      .then(res => setData(res))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [session, navigate]);

  if (!session) return null;

  const initials = session.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  const pct      = data?.progress?.pct ?? 0;
  const course   = data?.progress?.curriculumTitle ?? '—';

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>

      {/* ── Top bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
        <div className="max-w-lg mx-auto px-4">
          {/* Brand + sign out */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#2b2240,#3d2d58)', color: '#F0B429' }}>
                VT
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#94a3b8' }}>VistaTech</p>
                <p className="text-xs text-slate-500">Student Portal</p>
              </div>
            </div>
            <button onClick={() => { signOut(); navigate('/student/login'); }}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
              Sign out
            </button>
          </div>

          {/* Student identity */}
          <div className="flex items-center gap-4 pb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#F0B429 0%,#FF6535 100%)', color: '#fff' }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-xl leading-tight" style={{ color: '#0f172a' }}>{session.name}</p>
              <p className="text-xs text-slate-400 mt-0.5 truncate">{course}</p>
              {!loading && (
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex-1 max-w-[140px]"><ProgressBar pct={pct} /></div>
                  <span className="text-xs font-bold" style={{ color: '#F0B429' }}>{pct}% done</span>
                </div>
              )}
            </div>
          </div>

          {/* Tab bar (icons from a wider screen up, so five fit on a phone) */}
          <div className="flex gap-0 -mb-px">
            {TAB_CONFIG.map(t => {
              const toSend = t.key === 'tasks' ? (data?.tasks?.toSend || 0) : 0;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className="flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 py-3 px-0 text-[11px] sm:text-xs font-bold transition-all border-b-2 relative"
                  style={{
                    color: tab === t.key ? '#2b2240' : '#94a3b8',
                    borderColor: tab === t.key ? '#F0B429' : 'transparent',
                  }}>
                  <span className={`hidden sm:inline ${tab === t.key ? 'text-yellow-500' : 'text-slate-400'}`}>{t.icon}</span>
                  <span className="truncate">{t.label}</span>
                  {toSend > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold leading-4 text-white" style={{ background: '#ef4444' }}>{toSend}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-lg mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-5">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-2 border-slate-100" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-yellow-400 animate-spin" />
            </div>
            <p className="text-sm text-slate-400 font-medium">Loading your dashboard…</p>
          </div>
        ) : (
          <>
            {data?.feePayment && (
              <FeePaymentCard studentId={session.studentId} fee={data.feePayment}
                onChange={feePayment => setData((d: any) => ({ ...d, feePayment }))} />
            )}
            {/* Tasks Given / Done: the tasks on the Tasks tab (the counts mentors only
                typed in the class log before 28 Oct 2026 stay in the admin's reports) */}
            {tab === 'progress'   && <ProgressTab  progress={data?.progress || {}}
              stats={{ ...(data?.stats || {}), ...(data?.tasks ? { tasksGiven: data.tasks.given, tasksDone: data.tasks.done } : {}) }} />}
            {tab === 'tasks'      && <MyTasksTab token={session.studentToken} onSignOut={() => { signOut(); navigate('/student/login'); }} />}
            {tab === 'placement'  && <PlacementTab placement={data?.placement} tasks={data?.tasks} />}
            {tab === 'jobs'       && <JobBoardTab  studentId={session.studentId} />}
            {tab === 'complaints' && <ComplaintsTab studentId={session.studentId} />}
          </>
        )}
      </div>
    </div>
  );
}
